/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export type RunStatus =
  | 'open'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'skipped'
  | string;

export type ActiveRun = {
  id: string;
  status: RunStatus;
  templateRef: string | null;
  createdBy: string | null;
  createdAt: string | null;
  lastHeartbeatAt: string | null;
  orphaned: boolean;
};

export type RunList = {
  orphanAfterSeconds: number;
  forceCloseEnabled: boolean;
  builtAt: number;
  runs: ActiveRun[];
};

export type TaskEvent = {
  id: number | string;
  taskId?: string;
  type?: string;
  body?: { message?: string; stepId?: string; status?: string } & Record<
    string,
    unknown
  >;
  createdAt?: string;
};

// Module-level cache so the sidebar access probe and the page share one
// round-trip (same convention as useDependsOnWatcherAccess).
type Access = { reachable: boolean | undefined; canWrite: boolean };
let cached: Access | undefined;
let inflight: Promise<Access> | undefined;

export const useActiveRunsWatcherAccess = (): Access => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<Access>(
    cached ?? { reachable: undefined, canWrite: false },
  );

  useEffect(() => {
    if (cached !== undefined) {
      setState(cached);
      return undefined;
    }
    let active = true;
    inflight =
      inflight ??
      (async () => {
        try {
          const base = await discoveryApi.getBaseUrl('active-runs-watcher');
          const res = await fetchApi.fetch(`${base}/access`);
          if (!res.ok) return { reachable: false, canWrite: false };
          const json = await res.json().catch(() => ({}));
          return { reachable: true, canWrite: json.canWrite === true };
        } catch {
          return { reachable: false, canWrite: false };
        }
      })();
    inflight.then(result => {
      if (result.reachable && result.canWrite) cached = result;
      else inflight = undefined;
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return state;
};

export const useActiveRunsWatcherApi = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const base = await discoveryApi.getBaseUrl('active-runs-watcher');
      const res = await fetchApi.fetch(`${base}${path}`, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      return body;
    },
    [discoveryApi, fetchApi],
  );

  // No statuses -> backend defaults to in-flight (open,processing), which is
  // bounded and always includes orphans. Passing statuses pulls those on demand
  // (e.g. failed, for retry), capped server-side.
  const listRuns = useCallback(
    (statuses?: string[]): Promise<RunList> =>
      call(
        `/tasks${
          statuses && statuses.length
            ? `?status=${encodeURIComponent(statuses.join(','))}`
            : ''
        }`,
      ),
    [call],
  );

  const cancel = useCallback(
    (id: string) => call(`/tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
    [call],
  );

  const retry = useCallback(
    (id: string) => call(`/tasks/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
    [call],
  );

  const forceClose = useCallback(
    (id: string) =>
      call(`/tasks/${encodeURIComponent(id)}/force-close`, { method: 'POST' }),
    [call],
  );

  const events = useCallback(
    (id: string): Promise<{ events?: TaskEvent[] } | TaskEvent[]> =>
      call(`/tasks/${encodeURIComponent(id)}/events`),
    [call],
  );

  return useMemo(
    () => ({ listRuns, cancel, retry, forceClose, events }),
    [listRuns, cancel, retry, forceClose, events],
  );
};
